import { flux } from '@/core/flux.module';
import { PUSH_IGNORE, PUSH_WORKBENCH } from '@/core/flux.service';
import { logWarn } from '@/utilities/logger';
import type { ExternalAgentBuilderPrompt } from '@/agentBuilder/agentBuilder.types';
import type {
  AIMemory,
  AgentType,
  ChatAttachment,
  ChatMessage,
  ExternalPromptPayload,
  FontSize,
  ReasoningStyle,
  SelectedDocument,
} from '@/aiAssistant/aiAssistant.types';

const AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY = 'sqAiAssistantStoreActiveChat';
const AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY = 'sqAiAssistantStoreDisplayed';
const AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY = 'sqAiAssistantStoreDisplayedLastChoice';

export function setAIAssistantDisplayed(displayed: boolean, ignore = false) {
  if (ignore && typeof window !== 'undefined') {
    try {
      // Don't override a prior default:
      // - per-tab choice in sessionStorage, or
      // - cross-tab "last choice" in localStorage.
      if (
        window.sessionStorage.getItem(AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY) !== null ||
        window.localStorage.getItem(AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY) !== null
      ) {
        return;
      }
    } catch (error) {
      logWarn(`Failed to read AI Assistant displayed default state from storage: ${error}`);
    }
  }

  flux.dispatch('AI_ASSISTANT_DISPLAYED', { displayed }, ignore ? PUSH_IGNORE : PUSH_WORKBENCH);

  if (typeof window !== 'undefined') {
    try {
      window.sessionStorage.setItem(AI_ASSISTANT_DISPLAYED_SESSION_STORAGE_KEY, displayed ? 'true' : 'false');
      window.localStorage.setItem(AI_ASSISTANT_DISPLAYED_LAST_CHOICE_LOCAL_STORAGE_KEY, displayed ? 'true' : 'false');
    } catch (error) {
      logWarn(`Failed to persist AI Assistant displayed state to storage: ${error}`);
    }
  }
}

export function setAIAssistantMinimized(minimized: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_MINIMIZED', { minimized }, PUSH_WORKBENCH);
}

export function setAIAssistantMaximized(maximized: boolean) {
  flux.dispatch('AI_ASSISTANT_MAXIMIZED', { maximized }, PUSH_WORKBENCH);
}

export function setAIAssistantLibraryDisplayed(libraryDisplayed: boolean) {
  flux.dispatch('AI_ASSISTANT_LIBRARY_DISPLAYED', { libraryDisplayed }, PUSH_WORKBENCH);
}

export function setAIAssistantMessages(messages: ChatMessage[]) {
  flux.dispatch('AI_ASSISTANT_SET_MESSAGES', { messages }, PUSH_WORKBENCH);
}

export function setSelectedAgent(agent: AgentType) {
  flux.dispatch('AI_ASSISTANT_SET_SELECTED_AGENT', { agent }, PUSH_WORKBENCH);
}

export function setAIAssistantFontSize(fontSize: FontSize) {
  flux.dispatch('AI_ASSISTANT_SET_FONT_SIZE', { fontSize }, PUSH_WORKBENCH);
}
export function setAIAssistantExternalPrompt(payload: ExternalPromptPayload | undefined) {
  flux.dispatch('AI_ASSISTANT_EXTERNAL_PROMPT', payload);
  if (payload?.agent) {
    setSelectedAgent(payload.agent);
  }
}

export function openAIAssistantWithExternalPrompt(
  payload: ExternalPromptPayload,
  options?: { wasDisplayed?: boolean; mountDelayMs?: number },
) {
  const wasDisplayed = options?.wasDisplayed ?? false;
  const mountDelayMs = options?.mountDelayMs ?? 0;

  if (!wasDisplayed) {
    setAIAssistantDisplayed(true);
  }

  const shouldDelayPrompt = !wasDisplayed && mountDelayMs > 0;

  if (shouldDelayPrompt) {
    setTimeout(() => setAIAssistantExternalPrompt(payload), mountDelayMs);
    return;
  }

  setAIAssistantExternalPrompt(payload);
}

/**
 * Sets an external agent builder prompt that will open AI Assistant in agent builder mode
 * and automatically submit the prompt to the specified agent builder.
 * @param payload The agent builder and prompt to submit, or undefined to clear
 */
export function setAIAssistantExternalAgentBuilderPrompt(payload: ExternalAgentBuilderPrompt | undefined) {
  flux.dispatch('AI_ASSISTANT_EXTERNAL_AGENT_BUILDER_PROMPT', payload);
}

export function setAIAssistantReasoningStyle(reasoningStyle: ReasoningStyle) {
  flux.dispatch('AI_ASSISTANT_SET_REASONING_STYLE', { reasoningStyle }, PUSH_WORKBENCH);
}

export function setAIAssistantGenerateMemory(generateMemory: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_GENERATE_MEMORY', { generateMemory }, PUSH_WORKBENCH);
}

export function setAIAssistantStartNewChatOnOpen(startNewChatOnOpen: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_START_NEW_CHAT_ON_OPEN', { startNewChatOnOpen }, PUSH_WORKBENCH);
}

/**
 * Sets the preferred subsection of the AI preferences tab
 * Mainly for the redirecting of memories in the memory ui
 * @param subsection The subsection to set
 */
export function setAIAssistantPreferredSubsection(subsection: 'general' | 'memories' | 'privacy' | null) {
  flux.dispatch('AI_ASSISTANT_SET_PREFERRED_SUBSECTION', { subsection }, PUSH_WORKBENCH);
}

export function setAIAssistantSavedMemories(savedMemories: AIMemory[]) {
  flux.dispatch('AI_ASSISTANT_SET_SAVED_MEMORIES', { savedMemories }, PUSH_WORKBENCH);
}

export function addAIAssistantMemory(memory: AIMemory) {
  flux.dispatch('AI_ASSISTANT_ADD_MEMORY', { memory }, PUSH_WORKBENCH);
}

export function removeAIAssistantMemory(memoryId: string) {
  flux.dispatch('AI_ASSISTANT_REMOVE_MEMORY', { memoryId }, PUSH_WORKBENCH);
}

export function updateAIAssistantMemory(memory: AIMemory) {
  flux.dispatch('AI_ASSISTANT_UPDATE_MEMORY', { memory }, PUSH_WORKBENCH);
}

export function setAiAssistantRefreshHistory(refreshHistory: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_REFRESH_HISTORY', { refreshHistory });
}

export function setAiAssistantActiveChat(chat: { id: string; name: string } | undefined) {
  flux.dispatch('AI_ASSISTANT_SET_ACTIVE_CHAT', { chat });

  if (typeof window !== 'undefined') {
    try {
      if (chat) {
        window.sessionStorage.setItem(AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY, JSON.stringify(chat));
      } else {
        window.sessionStorage.removeItem(AI_ASSISTANT_ACTIVE_CHAT_SESSION_STORAGE_KEY);
      }
    } catch (error) {
      logWarn(`Failed to persist AI Assistant active chat to sessionStorage: ${error}`);
    }
  }
}

export function setAiAssistantHistorySearch(searchValue: string) {
  flux.dispatch('AI_ASSISTANT_SET_HISTORY_SEARCH', { searchValue }, PUSH_WORKBENCH);
}

/**
 * Sets the selected document to reference in the AI Assistant
 * @param document The document to reference or null to clear
 */
export function setSelectedDocument(document: SelectedDocument | SelectedDocument[] | null) {
  flux.dispatch('AI_ASSISTANT_SET_SELECTED_DOCUMENT', { document });
}

/**
 * Adds a draft attachment to the AI Assistant
 * @param attachment The attachment to add
 */
export function addDraftAttachment(attachment: ChatAttachment) {
  flux.dispatch('AI_ASSISTANT_ADD_DRAFT_ATTACHMENT', { attachment });
}

/**
 * Removes a draft attachment from the AI Assistant
 * @param attachmentId The ID of the attachment to remove
 */
export function removeDraftAttachment(attachmentId: string) {
  flux.dispatch('AI_ASSISTANT_REMOVE_DRAFT_ATTACHMENT', { attachmentId });
}

/**
 * Clears all draft attachments from the AI Assistant
 */
export function clearDraftAttachments() {
  flux.dispatch('AI_ASSISTANT_CLEAR_DRAFT_ATTACHMENTS', {});
}

export function setAIAssistantLibraryEditorState(id: string | null, libraryEditorDisplayed: boolean) {
  flux.dispatch('AI_ASSISTANT_SET_LIBRARY_EDITOR_STATE', { id, libraryEditorDisplayed }, PUSH_WORKBENCH);
}
